import React, { SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMinus, faPencilAlt, faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import NumberFormat from 'react-number-format';
import { Control, Controller, useFormContext } from 'react-hook-form';
import { ICartItem } from '../../index';
import VuiNumberFormat from '../../../../../vodea/@vodea-ui/components/VuiNumberFormat';
import _ from 'lodash';
import ModalCartItem from '../ModalCartItem';

interface Props {
  data: Partial<ICartItem>;
  index: number;
  customer: any;
  setData:  React.Dispatch<React.SetStateAction<ICartItem[]>>;
  handleRemove: () => void;
}

const SalesOrderCartItem: React.FC<Props> = ({ setData, handleRemove, index, data, customer }) => {
  const [isOpen, setIsOpen] = useState(false);
  // const watchQuantity = form.watch(`sales_order_items[${index}].quantity`);

  const handleIncrement = useCallback(() => {
    setData((prevState) => {
      const cloneState = [...prevState];
      cloneState[index].quantity = (data.quantity ?? 0) + 1;
      return cloneState;
    });
  }, [index, data]);

  const handleDecrement = useCallback(() => {
    setData((prevState) => {
      const cloneState = [...prevState];
      cloneState[index].quantity = (data.quantity ?? 0) - 1;
      return cloneState;
    });
  }, [index, data]);

  const handleChangeQuantity = useCallback((value: number) => {
    setData((prevState) => {
      const cloneState = [...prevState];
      cloneState[index].quantity = value;
      return cloneState;
    });
  }, [index, data]);

  const handleEdit = useCallback(() => {
    setIsOpen(true);
  }, []);

  const handleSubmit = useCallback((value: Partial<ICartItem>) => {
    setData((prevState) => {
      const cloneState = [...prevState];
      cloneState[index].quantity = value.quantity ?? 0;
      cloneState[index].price = value.price ?? 0;
      cloneState[index].discount = value.discount ?? 0;
      cloneState[index].discount_product = value.discount_product ?? 0;
      cloneState[index].total_discount = value.total_discount ?? 0;
      cloneState[index].storage_id = value.storage_id ?? null;
      cloneState[index].unit_id = value.unit_id ?? null;
      cloneState[index].total = value.total ?? 0;
      return cloneState;
    });
  }, []);

  const calculateTotal = useCallback(() => {
    const quantity = data.quantity ?? 0;
    const totalDiscountProduct = quantity * (data?.discount_product ?? 0);
    const total = Number(quantity ?? 0) * (data.price ?? 0) - (data.discount ?? 0) - totalDiscountProduct;
    setData((prevState) => {
      const cloneState = [...prevState];
      cloneState[index].total = total;
      return cloneState;
    });
  }, [data, index]);

  useMemo(() => {
    calculateTotal();
  }, [data.quantity]);
  
  const renderTotalDiscount = useMemo(() => {
    const total = (data.total_discount ?? 0) + (data.discount ?? 0);
    return (
      <div className='so-cart-item-value'>
        <VuiNumberFormat data={total} value={total} prefix='IDR ' />
      </div>
    );
  }, [data.total_discount, data.discount]);

  const renderTotal = useMemo(() => {
    const total = data.total ?? 0;
    return (
      <div className='so-cart-item-value'>
        <VuiNumberFormat data={total} value={total} prefix='IDR ' />
      </div>
    );
  }, [data.total]);

  return (
    <>
      <ModalCartItem
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        selectedData={data}
        customer={customer}
        onSubmit={value => {
          handleSubmit(value);
        }}
      />

      <div className='so-cart-item'>
        <Row className='so-cart-title'>
          <Col xs={8}>
            <div className='so-cart-item-name'>{data.product_id?.name}</div>
          </Col>
          <Col xs={4}>
            <div className='so-cart-item-price'>
              <VuiNumberFormat prefix='IDR ' data={data?.price ?? 0} suffix={`/${_.get(data, 'unit_id.code')}`} />
            </div>
          </Col>
        </Row>
        <Row className='so-cart-item-footer'>
          <Col xs={8}>
            <div className='so-cart-item-details'>
              <div className='d-flex justify-content-between'>
                <div className='so-cart-item-label'>Storage</div>
                <div className='so-cart-item-value'>{_.get(data, 'storage_id.name', '-')}</div>
              </div>
              <div className='d-flex justify-content-between'>
                <div className='so-cart-item-label'>Discount</div>
                {renderTotalDiscount}
              </div>
              <div className='d-flex justify-content-between'>
                <div className='so-cart-item-label'>Total</div>
                {renderTotal}
              </div>
            </div>
          </Col>
          <Col xs={4}>
            <div className='so-cart-item-action'>
              <div className='so-cart-item-edit'>
                {(data.quantity ?? 0) > 1 ? (
                  <div className="so-cart-item-icon-wrapper delete-icon">
                    <FontAwesomeIcon icon={faTrash} onClick={handleRemove} />
                  </div>
                ): null}
                <div className="so-cart-item-icon-wrapper edit-icon">
                  <FontAwesomeIcon icon={faPencilAlt} onClick={handleEdit} />
                </div>
              </div>
              <div className='so-cart-item-quantity'>
                <div>
                  <div className='so-cart-item-quantity-icon left'>
                    {(data.quantity ?? 0) > 1 ? (
                      <FontAwesomeIcon icon={faMinus} onClick={handleDecrement} />
                    ) : (
                      <FontAwesomeIcon icon={faTrash} onClick={handleRemove} />
                    )}
                  </div>
                </div>
                <div>
                  <NumberFormat
                    className='form-control format-quantity text-center'
                    value={data.quantity}
                    allowNegative={false}
                    thousandSeparator={'.'}
                    decimalSeparator={','}
                    decimalScale={0}
                    placeholder={'0'}
                    onValueChange={({ value }) => {
                      handleChangeQuantity(Number(value));
                    }}
                    height='24px'
                  />
                </div>
                <div>
                  <div className='so-cart-item-quantity-icon right'>
                    <FontAwesomeIcon icon={faPlus} onClick={handleIncrement} />
                  </div>
                </div>
              </div>
            </div>
          </Col>
        </Row>
      </div>
    </>
  );
};

export default React.memo(SalesOrderCartItem);